// Lightweight, asset-free notification chimes built on the Web Audio API.
// Shared by the admin error-log bell (reported bugs) and the processed-model failed-run
// notifications so each surface can play a recognisably different tone.

// Lazily-created shared AudioContext so we can play a short chime without a sound asset.
let audioCtx: AudioContext | null = null;

const getCtx = (): AudioContext | null => {
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctx) return null;
    if (!audioCtx) audioCtx = new Ctx();
    if (audioCtx.state === "suspended") audioCtx.resume();
    return audioCtx;
  } catch {
    return null;
  }
};

// Play a sequence of sine-wave tones (a short "ding-dong"-style chime).
const playTones = (frequencies: number[]) => {
  const ctx = getCtx();
  if (!ctx) return;
  try {
    const now = ctx.currentTime;
    frequencies.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const start = now + i * 0.16;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.18, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.15);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.16);
    });
  } catch {
    // Audio is best-effort; ignore failures (e.g. autoplay restrictions).
  }
};

// Reported error log chime — bright rising two-tone "ding-dong".
export const playErrorLogChime = () => playTones([880, 1175]);

// Processed-model failed-run chime — deliberately lower/distinct so admins can tell it apart
// from the reported-error chime.
export const playFailedRunChime = () => playTones([523, 392]);
